import { useMemo, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

import type { FormattedAttachment } from "../../hooks/useAttachments"
import type { ViewerImage } from "../image-viewer/image-viewer"
import type { AttachmentItemVariant } from "./attachment-item"
import { ImageViewer } from "../image-viewer/image-viewer"
import { AttachmentItem } from "./attachment-item"

const toViewerSrc = (attachment: FormattedAttachment): string => {
	const url = attachment.Content
	if (url?.includes("http") || url?.startsWith("data:")) return url
	return `data:${attachment.ContentType};base64,${url}`
}

export function AttachmentsList({
	attachments,
	className,
	variant = "default",
	enableViewer = false,
	showPointer = true,
}: {
	attachments: FormattedAttachment[]
	className?: string
	variant?: AttachmentItemVariant
	enableViewer?: boolean
	showPointer?: boolean
}) {
	const imageAttachments = attachments.filter((attachment) =>
		attachment.ContentType?.startsWith("image"),
	)
	const videoAttachments = attachments.filter((attachment) =>
		attachment.ContentType?.startsWith("video"),
	)

	const otherAttachments = attachments.filter(
		(attachment) =>
			!attachment.ContentType?.startsWith("image") &&
			!attachment.ContentType?.startsWith("video"),
	)

	const [viewer, setViewer] = useState<{
		index: number
		originRect: DOMRect
	} | null>(null)

	const viewerImages = useMemo<ViewerImage[]>(
		() =>
			imageAttachments.map((attachment) => ({
				src: toViewerSrc(attachment),
				alt: attachment.Name,
				name: attachment.Name,
			})),
		[imageAttachments],
	)

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			{videoAttachments.length > 0 && (
				<div className="flex flex-col gap-2">
					{videoAttachments.map((attachment) => (
						<AttachmentItem
							attachment={attachment}
							variant={variant}
							key={attachment.ContentID}
							showTooltip={false}
							showPointer={showPointer}
						/>
					))}
				</div>
			)}

			{imageAttachments.length > 0 && (
				<div className="flex flex-row flex-wrap items-center gap-2">
					{imageAttachments.map((attachment, index) => (
						<AttachmentItem
							attachment={attachment}
							variant={variant}
							key={attachment.ContentID}
							enableViewer={enableViewer}
							showPointer={showPointer}
							onImageClick={(originRect) => setViewer({ index, originRect })}
						/>
					))}
				</div>
			)}

			{otherAttachments.length > 0 && (
				<div className="flex flex-row flex-wrap items-center gap-2">
					{otherAttachments.map((attachment) => (
						<AttachmentItem
							attachment={attachment}
							variant={variant}
							key={attachment.ContentID}
							showPointer={showPointer}
						/>
					))}
				</div>
			)}

			{enableViewer && viewer !== null && (
				<ImageViewer
					images={viewerImages}
					index={viewer.index}
					originRect={viewer.originRect}
					onIndexChange={(index) => setViewer((v) => (v ? { ...v, index } : v))}
					onClose={() => setViewer(null)}
				/>
			)}
		</div>
	)
}
